import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Button,
  TextField,
  Typography,
  IconButton,
  Tabs,
  Tab,
  Radio,
  RadioGroup,
  FormControlLabel,
  CircularProgress,
  Avatar,
  Paper,
  MenuItem,
  Checkbox,
  Tooltip,
} from '@mui/material';
import {
  Close,
  Star,
  StarBorder,
  Lightbulb,
  AttachMoney,
  Receipt,
  CalendarMonth,
  ConfirmationNumber,
  TaskAlt,
  ShoppingCart,
  ChevronLeft,
  ChevronRight,
  Person,
  Business,
  ContentCopy,
  Delete,
} from '@mui/icons-material';
import { customerAPI, paymentTermAPI, activityLogAPI } from '../../services/api';
import { toast } from 'react-toastify';
import { useAuth } from '../../context/AuthContext';
import { ActivityPanel, StatusChip, SalespersonSelect, ConfirmDialog } from '../../components/common';
import { isDummyEmail } from '../../utils/helpers';

const countryOptions = ['India', 'United States', 'United Kingdom', 'Canada', 'Australia'];

const initialFormData = {
  type: 'person',
  name: '',
  email: '',
  phone: '',
  company: '',
  street: '',
  city: '',
  zip: '',
  country: 'India',
  gstin: '',
  internalNotes: '',
  status: 'lead',
  salesperson: '',
  paymentTerms: '',
  paymentMethod: '',
  pricelist: 'default_inr',
  deliveryMethod: '',
  fiscalPosition: '',
  buyer: '',
  purchasePaymentTerms: '',
  purchasePaymentMethod: '',
  receiptReminder: false,
  supplierCurrency: '',
  reference: '',
  slaPolicies: '',
  banks: '',
  reminders: 'automatic',
  isFavorite: false,
};

const CustomerView = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { user } = useAuth();
  
  const [formData, setFormData] = useState(initialFormData);
  const [originalFormData, setOriginalFormData] = useState(initialFormData);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [duplicating, setDuplicating] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const [paymentTermsList, setPaymentTermsList] = useState([]);

  const fetchCustomer = useCallback(async (signal) => {
    try {
      setLoading(true);
      const response = await customerAPI.getById(id);
      if (!signal?.aborted) {
        const customer = response.data;
        const email = customer.email && !isDummyEmail(customer.email) ? customer.email : '';
        setFormData({
          type: customer.type || (customer.customerType === 'business' || customer.customerType === 'enterprise' ? 'company' : 'person'),
          name: customer.name || '',
          email: email,
          phone: customer.phone || customer.mobile || '',
          company: customer.company || '',
          street: customer.address?.street || '',
          city: customer.address?.city || '',
          zip: customer.address?.zip || customer.address?.zipCode || '',
          country: customer.address?.country || 'India',
          gstin: customer.gstin || customer.gstNumber || '',
          internalNotes: customer.internalNotes || customer.notes || '',
          status: customer.status || 'lead',
          salesperson: customer.assignedSalesPerson?._id || customer.assignedSalesPerson || '',
          paymentTerms: customer.paymentTerms || '',
          paymentMethod: customer.paymentMethod || '',
          pricelist: customer.pricelist || 'default_inr',
          deliveryMethod: customer.deliveryMethod || '',
          fiscalPosition: customer.fiscalPosition || '',
          buyer: customer.buyer || '',
          purchasePaymentTerms: customer.purchasePaymentTerms || '',
          purchasePaymentMethod: customer.purchasePaymentMethod || '',
          receiptReminder: customer.receiptReminder || false,
          supplierCurrency: customer.supplierCurrency || '',
          reference: customer.reference || '',
          slaPolicies: customer.slaPolicies || '',
          banks: customer.banks || '',
          reminders: customer.reminders || 'automatic',
          isFavorite: customer.isFavorite || false,
          createdAt: customer.createdAt,
          updatedAt: customer.updatedAt,
          customerType: customer.customerType,
          leadSource: customer.leadSource,
        });
        setOriginalFormData({
          type: customer.type || (customer.customerType === 'business' || customer.customerType === 'enterprise' ? 'company' : 'person'),
          name: customer.name || '',
          email: email,
          phone: customer.phone || customer.mobile || '',
          company: customer.company || '',
          street: customer.address?.street || '',
          city: customer.address?.city || '',
          zip: customer.address?.zip || customer.address?.zipCode || '',
          country: customer.address?.country || 'India',
          gstin: customer.gstin || customer.gstNumber || '',
          internalNotes: customer.internalNotes || customer.notes || '',
          status: customer.status || 'lead',
        });
      }
    } catch (error) {
      if (!signal?.aborted) {
        toast.error('Failed to load customer');
        navigate('/customers');
      }
    } finally {
      if (!signal?.aborted) {
        setLoading(false);
      }
    }
  }, [id, navigate]);

  const fetchPaymentTerms = async () => {
    try {
      const response = await paymentTermAPI.getAllForDropdown();
      setPaymentTermsList(response.data);
    } catch (error) {
      console.error('Error fetching payment terms:', error);
    }
  };

  useEffect(() => {
    fetchPaymentTerms();
    const abortController = new AbortController();
    fetchCustomer(abortController.signal);
    return () => abortController.abort();
  }, [fetchCustomer]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  const handleToggleFavorite = () => {
    setFormData((prev) => ({ ...prev, isFavorite: !prev.isFavorite }));
  };

  const handleSubmit = async () => {
    if (!formData.name.trim()) {
      toast.error('Name is required');
      return;
    }

    try {
      setSaving(true);
      const payload = {
        type: formData.type,
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        company: formData.type === 'person' ? formData.company : formData.name,
        address: {
          street: formData.street,
          city: formData.city,
          zipCode: formData.zip,
          country: formData.country,
        },
        gstNumber: formData.gstin,
        internalNotes: formData.internalNotes,
        status: formData.status,
        isFavorite: formData.isFavorite,
        assignedSalesPerson: formData.salesperson || null,
      };

      await customerAPI.update(id, payload);
      toast.success('Customer updated successfully');
      setOriginalFormData({ ...formData });
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to update customer');
    } finally {
      setSaving(false);
    }
  };

  const handleDiscard = () => {
    setFormData({ ...originalFormData });
    toast.info('Changes discarded');
  };

  const handleDuplicate = async () => {
    try {
      setDuplicating(true);
      const response = await customerAPI.duplicate(id);
      const newCustomer = response.data.customer;
      toast.success(`Customer duplicated as "${newCustomer.name}"`);
      navigate(`/customers/${newCustomer._id}`);
    } catch (error) {
      console.error('Duplicate error:', error);
      toast.error(error.response?.data?.message || 'Failed to duplicate customer');
    } finally {
      setDuplicating(false);
    }
  };

  const handleDelete = () => {
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    try {
      setDeleting(true);
      await customerAPI.delete(id);
      toast.success('Customer deleted successfully');
      navigate('/customers');
    } catch (error) {
      toast.error('Failed to delete customer');
    } finally {
      setDeleting(false);
      setDeleteDialogOpen(false);
    }
  };

  const hasChanges = JSON.stringify(formData) !== JSON.stringify(originalFormData);

  const formatDate = (date) => {
    if (!date) return '-';
    return new Date(date).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ backgroundColor: '#f5f5f5', minHeight: 'calc(100vh - 64px)' }}>
      {/* Header Bar */}
      <Box
        sx={{
          backgroundColor: 'white',
          borderBottom: '1px solid #dee2e6',
          px: 2,
          py: 0.75,
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          flexWrap: 'nowrap',
          overflowX: 'auto',
        }}
      >
        {/* Left - New button and breadcrumb */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexShrink: 0 }}>
          <Button
            variant="outlined"
            onClick={() => navigate('/customers/new')}
            sx={{
              borderColor: '#dee2e6',
              color: '#333',
              textTransform: 'none',
              fontWeight: 500,
              fontSize: '14px',
              px: 2,
              py: 0.5,
              minHeight: 32,
              '&:hover': { borderColor: '#875A7B', backgroundColor: 'transparent' },
            }}
          >
            New
          </Button>
          <Box sx={{ display: 'flex', flexDirection: 'column', lineHeight: 1.2 }}>
            <Typography sx={{ fontSize: '13px', color: '#875A7B', fontWeight: 500, cursor: 'pointer' }} onClick={() => navigate('/customers')}>
              Customers
            </Typography>
            <Typography sx={{ fontSize: '12px', color: '#333', fontWeight: 500 }}>
              {formData.name || 'Edit Customer'}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
            <Tooltip title="Duplicate customer">
              <IconButton 
                size="small" 
                sx={{ p: 0.25 }} 
                onClick={handleDuplicate}
                disabled={duplicating}
              >
                <ContentCopy sx={{ fontSize: 16, color: duplicating ? '#ccc' : '#666' }} />
              </IconButton>
            </Tooltip>
            <Tooltip title="Delete customer">
              <IconButton 
                size="small" 
                sx={{ p: 0.25 }} 
                onClick={handleDelete}
              >
                <Delete sx={{ fontSize: 16, color: '#d32f2f' }} />
              </IconButton>
            </Tooltip>
            <IconButton size="small" sx={{ p: 0.25 }} onClick={() => navigate('/customers')}>
              <Close sx={{ fontSize: 16, color: '#666' }} />
            </IconButton>
          </Box>
        </Box>

        {/* Stats buttons */}
        <Box sx={{ display: 'flex', alignItems: 'stretch', gap: 0, flexShrink: 0, border: '1px solid #dee2e6', borderRadius: '4px', overflow: 'hidden', ml: 'auto' }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.75,
              px: 1.5,
              py: 0.5,
              cursor: 'pointer',
              borderRight: '1px solid #dee2e6',
              '&:hover': { bgcolor: '#f8f9fa' },
            }}
          >
            <Lightbulb sx={{ fontSize: 18, color: '#f5c518' }} />
            <Box sx={{ display: 'flex', flexDirection: 'column', lineHeight: 1.2 }}>
              <Typography sx={{ fontSize: '11px', color: '#333' }}>Opportunities</Typography>
              <Typography sx={{ fontSize: '10px', color: '#666' }}>0</Typography>
            </Box>
          </Box>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.75,
              px: 1.5,
              py: 0.5,
              cursor: 'pointer',
              borderRight: '1px solid #dee2e6',
              '&:hover': { bgcolor: '#f8f9fa' },
            }}
          >
            <AttachMoney sx={{ fontSize: 18, color: '#28a745' }} />
            <Box sx={{ display: 'flex', flexDirection: 'column', lineHeight: 1.2 }}>
              <Typography sx={{ fontSize: '11px', color: '#333' }}>Sales</Typography>
              <Typography sx={{ fontSize: '10px', color: '#666' }}>0</Typography>
            </Box>
          </Box>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.75,
              px: 1.5,
              py: 0.5,
              cursor: 'pointer',
              borderRight: '1px solid #dee2e6',
              '&:hover': { bgcolor: '#f8f9fa' },
            }}
          >
            <Receipt sx={{ fontSize: 18, color: '#875A7B' }} />
            <Box sx={{ display: 'flex', flexDirection: 'column', lineHeight: 1.2 }}>
              <Typography sx={{ fontSize: '11px', color: '#333' }}>Invoiced</Typography>
              <Typography sx={{ fontSize: '10px', color: '#666' }}>₹ 0.00</Typography>
            </Box>
          </Box>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.75,
              px: 1.5,
              py: 0.5,
              cursor: 'pointer',
              borderRight: '1px solid #dee2e6',
              '&:hover': { bgcolor: '#f8f9fa' },
            }}
          >
            <CalendarMonth sx={{ fontSize: 18, color: '#17a2b8' }} />
            <Box sx={{ display: 'flex', flexDirection: 'column', lineHeight: 1.2 }}>
              <Typography sx={{ fontSize: '11px', color: '#333' }}>Meetings</Typography>
              <Typography sx={{ fontSize: '10px', color: '#666' }}>0</Typography>
            </Box>
          </Box>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.75,
              px: 1.5,
              py: 0.5,
              cursor: 'pointer',
              borderRight: '1px solid #dee2e6',
              '&:hover': { bgcolor: '#f8f9fa' },
            }}
          >
            <ConfirmationNumber sx={{ fontSize: 18, color: '#dc3545' }} />
            <Box sx={{ display: 'flex', flexDirection: 'column', lineHeight: 1.2 }}>
              <Typography sx={{ fontSize: '11px', color: '#333' }}>Tickets</Typography>
              <Typography sx={{ fontSize: '10px', color: '#666' }}>0</Typography>
            </Box>
          </Box>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.75,
              px: 1.5,
              py: 0.5,
              cursor: 'pointer',
              borderRight: '1px solid #dee2e6',
              '&:hover': { bgcolor: '#f8f9fa' },
            }}
          >
            <TaskAlt sx={{ fontSize: 18, color: '#6f42c1' }} />
            <Box sx={{ display: 'flex', flexDirection: 'column', lineHeight: 1.2 }}>
              <Typography sx={{ fontSize: '11px', color: '#333' }}>Tasks</Typography>
              <Typography sx={{ fontSize: '10px', color: '#666' }}>0</Typography>
            </Box>
          </Box>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.75,
              px: 1.5,
              py: 0.5,
              cursor: 'pointer',
              '&:hover': { bgcolor: '#f8f9fa' },
            }}
          >
            <ShoppingCart sx={{ fontSize: 18, color: '#fd7e14' }} />
            <Box sx={{ display: 'flex', flexDirection: 'column', lineHeight: 1.2 }}>
              <Typography sx={{ fontSize: '11px', color: '#333' }}>Purchases</Typography>
              <Typography sx={{ fontSize: '10px', color: '#666' }}>0</Typography>
            </Box>
          </Box>
        </Box>

        {/* Pagination */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0, ml: 1 }}>
          <Typography sx={{ fontSize: '13px', color: '#666' }}>1 / 1</Typography>
          <IconButton size="small" disabled sx={{ p: 0.25 }}>
            <ChevronLeft sx={{ fontSize: 18, color: '#ccc' }} />
          </IconButton>
          <IconButton size="small" disabled sx={{ p: 0.25 }}>
            <ChevronRight sx={{ fontSize: 18, color: '#ccc' }} />
          </IconButton>
        </Box>
      </Box>

      {/* Main Content */}
      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', lg: 'row' }, p: 2, gap: 2 }}>
        {/* Left - Customer Details */}
        <Box sx={{ flex: 1 }}>
          <Paper sx={{ p: 3, border: '1px solid #dee2e6', boxShadow: 'none' }}>
            {/* Person/Company Toggle */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
              <RadioGroup
                row
                value={formData.type}
                onChange={(e) => setFormData({ ...formData, type: e.target.value })}
              >
                <FormControlLabel
                  value="person"
                  control={
                    <Radio
                      size="small"
                      sx={{
                        color: formData.type === 'person' ? '#00A09D' : '#6c757d',
                        '&.Mui-checked': { color: '#00A09D' },
                      }}
                    />
                  }
                  label={<Typography sx={{ fontSize: '14px', color: '#333' }}>Person</Typography>}
                />
                <FormControlLabel
                  value="company"
                  control={
                    <Radio
                      size="small"
                      sx={{
                        color: formData.type === 'company' ? '#00A09D' : '#6c757d',
                        '&.Mui-checked': { color: '#00A09D' },
                      }}
                    />
                  }
                  label={<Typography sx={{ fontSize: '14px', color: '#333' }}>Company</Typography>}
                />
              </RadioGroup>
            </Box>

            {/* Customer Header - Avatar and Basic Info */}
            <Box sx={{ display: 'flex', gap: 3, mb: 3 }}>
              {/* Avatar */}
              <Avatar
                sx={{
                  width: 110,
                  height: 110,
                  bgcolor: formData.name ? '#5c6bc0' : '#f8f9fa',
                  fontSize: '48px',
                  fontWeight: 400,
                  borderRadius: '4px',
                  border: '1px solid #dee2e6',
                  cursor: 'pointer',
                }}
              >
                {formData.name ? formData.name.charAt(0).toUpperCase() : (
                  formData.type === 'person' ?
                    <Person sx={{ fontSize: 60, color: '#adb5bd' }} /> :
                    <Business sx={{ fontSize: 60, color: '#adb5bd' }} />
                )}
              </Avatar>

              {/* Name and Contact Info */}
              <Box sx={{ flex: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <IconButton size="small" onClick={handleToggleFavorite} sx={{ p: 0 }}>
                    {formData.isFavorite ? (
                      <Star sx={{ fontSize: 24, color: '#f5c518' }} />
                    ) : (
                      <StarBorder sx={{ fontSize: 24, color: '#999' }} />
                    )}
                  </IconButton>
                  <TextField
                    fullWidth
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    placeholder={formData.type === 'person' ? 'e.g. Brandon Freeman' : 'e.g. Lumber Inc'}
                    variant="standard"
                    InputProps={{
                      disableUnderline: true,
                      sx: {
                        fontSize: '28px',
                        fontWeight: 400,
                        color: '#333',
                      },
                    }}
                    sx={{ borderBottom: '1px solid #dee2e6' }}
                  />
                  <StatusChip status={formData.status} sx={{ ml: 1 }} />
                </Box>
                
                {/* Email - Editable */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                  <Typography sx={{ fontSize: '14px', color: '#666' }}>✉</Typography>
                  <TextField
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="email@example.com"
                    variant="standard"
                    size="small"
                    InputProps={{
                      disableUnderline: true,
                      sx: { fontSize: '14px', color: '#017e84' },
                    }}
                    sx={{ flex: 1, borderBottom: '1px solid #dee2e6' }}
                  />
                </Box>
                
                {/* Phone - Editable */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography sx={{ fontSize: '14px', color: '#666' }}>📞</Typography>
                  <TextField
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    placeholder="+1 234-567-8900"
                    variant="standard"
                    size="small"
                    InputProps={{
                      disableUnderline: true,
                      sx: { fontSize: '14px', color: '#333' },
                    }}
                    sx={{ flex: 1, borderBottom: '1px solid #dee2e6' }}
                  />
                </Box>
              </Box>
            </Box>

            {/* Two Column Layout */}
            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 4, mb: 3 }}>
              {/* Left Column */}
              <Box sx={{ flex: 1 }}>
                {/* Company (only for Person type) */}
                {formData.type === 'person' && (
                  <Box sx={{ display: 'flex', mb: 2 }}>
                    <Typography sx={{ width: 100, fontSize: '14px', fontWeight: 500, color: '#333' }}>
                      Company
                    </Typography>
                    <TextField
                      name="company"
                      value={formData.company}
                      onChange={handleChange}
                      placeholder="Company Name..."
                      variant="standard"
                      size="small"
                      fullWidth
                      InputProps={{
                        disableUnderline: true,
                        sx: { fontSize: '14px' },
                      }}
                      sx={{ flex: 1, borderBottom: '1px solid #dee2e6' }}
                    />
                  </Box>
                )}

                {/* Address Section */}
                <Box sx={{ display: 'flex', mb: 2 }}>
                  <Typography sx={{ width: 100, fontSize: '14px', fontWeight: 500, color: '#333', pt: 0.5 }}>
                    Address
                  </Typography>
                  <Box sx={{ flex: 1 }}>
                    <TextField
                      name="street"
                      value={formData.street}
                      onChange={handleChange}
                      placeholder="Street..."
                      variant="standard"
                      size="small"
                      fullWidth
                      InputProps={{
                        disableUnderline: true,
                        sx: { fontSize: '14px' },
                      }}
                      sx={{ borderBottom: '1px solid #dee2e6', mb: 1 }}
                    />
                    <Box sx={{ display: 'flex', gap: 2, mb: 1 }}>
                      <TextField
                        name="city"
                        value={formData.city}
                        onChange={handleChange}
                        placeholder="City"
                        variant="standard"
                        size="small"
                        InputProps={{
                          disableUnderline: true,
                          sx: { fontSize: '14px' },
                        }}
                        sx={{ flex: 1, borderBottom: '1px solid #dee2e6' }}
                      />
                      <TextField
                        name="zip"
                        value={formData.zip}
                        onChange={handleChange}
                        placeholder="ZIP"
                        variant="standard"
                        size="small"
                        InputProps={{
                          disableUnderline: true,
                          sx: { fontSize: '14px' },
                        }}
                        sx={{ width: 80, borderBottom: '1px solid #dee2e6' }}
                      />
                    </Box>
                    <TextField
                      select
                      name="country"
                      value={formData.country}
                      onChange={handleChange}
                      variant="standard"
                      size="small"
                      InputProps={{
                        disableUnderline: true,
                        sx: { fontSize: '14px' },
                      }}
                      sx={{ width: 180, borderBottom: '1px solid #dee2e6' }}
                    >
                      {countryOptions.map((country) => (
                        <MenuItem key={country} value={country}>
                          {country}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Box>
                </Box>
              </Box>

              {/* Right Column */}
              <Box sx={{ flex: 1 }}>
                {/* GSTIN */}
                <Box sx={{ display: 'flex', mb: 2 }}>
                  <Typography sx={{ width: 80, fontSize: '14px', fontWeight: 500, color: '#333' }}>
                    GSTIN <Typography component="span" sx={{ fontSize: '12px', color: '#999' }}>?</Typography>
                  </Typography>
                  <TextField
                    name="gstin"
                    value={formData.gstin}
                    onChange={handleChange}
                    placeholder="not applicable"
                    variant="standard"
                    size="small"
                    fullWidth
                    InputProps={{
                      disableUnderline: true,
                      sx: { fontSize: '14px' },
                    }}
                    sx={{ flex: 1, borderBottom: '1px solid #dee2e6' }}
                  />
                </Box>
              </Box>
            </Box>

            {/* Tabs */}
            <Tabs
              value={activeTab}
              onChange={(e, v) => setActiveTab(v)}
              variant="scrollable"
              scrollButtons="auto"
              allowScrollButtonsMobile
              sx={{
                borderBottom: '1px solid #dee2e6',
                mb: 3,
                '& .MuiTab-root': {
                  textTransform: 'none',
                  fontSize: '14px',
                  minHeight: 40,
                  color: '#666',
                },
                '& .Mui-selected': { color: '#333', fontWeight: 500 },
                '& .MuiTabs-indicator': { backgroundColor: '#875A7B' },
              }}
            >
              <Tab label="Contacts" />
              <Tab label="Sales & Purchase" />
              <Tab label="Invoicing" />
              <Tab label="Notes" />
            </Tabs>

            {/* Tab Content */}
            {activeTab === 0 && (
              <Box>
                <Button
                  variant="text"
                  sx={{
                    color: '#017e84',
                    textTransform: 'none',
                    fontSize: '14px',
                    p: 2,
                    border: '1px dashed #dee2e6',
                    borderRadius: '4px',
                    width: { xs: '100%', sm: 'auto' },
                    minWidth: 200,
                  }}
                >
                  Add Contact
                </Button>
              </Box>
            )}

            {activeTab === 1 && (
              <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 4 }}>
                {/* Left Column - Sales */}
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography sx={{ fontWeight: 600, color: '#333', fontSize: '13px', mb: 2, pb: 1, borderBottom: '1px solid #e0e0e0' }}>
                    SALES
                  </Typography>
                  <Box sx={{ mb: 3 }}>
                    <SalespersonSelect
                      value={formData.salesperson}
                      onChange={(salespersonId) => setFormData({ ...formData, salesperson: salespersonId })}
                    />
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                      <Typography sx={{ width: 140, color: '#495057', fontSize: '13px' }}>Payment Terms</Typography>
                      <TextField
                        select
                        name="paymentTerms"
                        value={formData.paymentTerms}
                        onChange={handleChange}
                        variant="standard"
                        size="small"
                        fullWidth
                        InputProps={{ disableUnderline: true, sx: { fontSize: '13px' } }}
                        sx={{ borderBottom: '1px solid #dee2e6' }}
                      >
                        <MenuItem value="">Select...</MenuItem>
                        {paymentTermsList.map((term) => (
                          <MenuItem key={term._id} value={term._id}>
                            {term.name}
                          </MenuItem>
                        ))}
                      </TextField>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                      <Typography sx={{ width: 140, color: '#495057', fontSize: '13px' }}>Payment Method</Typography>
                      <TextField
                        select
                        name="paymentMethod"
                        value={formData.paymentMethod}
                        onChange={handleChange}
                        variant="standard"
                        size="small"
                        fullWidth
                        InputProps={{ disableUnderline: true, sx: { fontSize: '13px' } }}
                        sx={{ borderBottom: '1px solid #dee2e6' }}
                      >
                        <MenuItem value="">Select...</MenuItem>
                        <MenuItem value="bank_transfer">Bank Transfer</MenuItem>
                        <MenuItem value="credit_card">Credit Card</MenuItem>
                        <MenuItem value="cash">Cash</MenuItem>
                        <MenuItem value="upi">UPI</MenuItem>
                        <MenuItem value="cheque">Cheque</MenuItem>
                      </TextField>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                      <Typography sx={{ width: 140, color: '#495057', fontSize: '13px' }}>
                        Pricelist <sup style={{ color: '#875A7B', cursor: 'help' }}>?</sup>
                      </Typography>
                      <TextField
                        select
                        name="pricelist"
                        value={formData.pricelist}
                        onChange={handleChange}
                        variant="standard"
                        size="small"
                        fullWidth
                        InputProps={{ disableUnderline: true, sx: { fontSize: '13px' } }}
                        sx={{ borderBottom: '1px solid #dee2e6' }}
                      >
                        <MenuItem value="default_inr">Default (INR)</MenuItem>
                        <MenuItem value="wholesale">Wholesale</MenuItem>
                        <MenuItem value="retail">Retail</MenuItem>
                      </TextField>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Typography sx={{ width: 140, color: '#495057', fontSize: '13px' }}>
                        Delivery Method <sup style={{ color: '#875A7B', cursor: 'help' }}>?</sup>
                      </Typography>
                      <TextField
                        select
                        name="deliveryMethod"
                        value={formData.deliveryMethod}
                        onChange={handleChange}
                        variant="standard"
                        size="small"
                        fullWidth
                        InputProps={{ disableUnderline: true, sx: { fontSize: '13px' } }}
                        sx={{ borderBottom: '1px solid #dee2e6' }}
                      >
                        <MenuItem value="">Select...</MenuItem>
                        <MenuItem value="pickup">Pickup</MenuItem>
                        <MenuItem value="delivery">Delivery</MenuItem>
                        <MenuItem value="shipping">Shipping</MenuItem>
                      </TextField>
                    </Box>
                  </Box>

                  {/* Fiscal Information */}
                  <Typography sx={{ fontWeight: 600, color: '#333', fontSize: '13px', mb: 2, pb: 1, borderBottom: '1px solid #e0e0e0' }}>
                    FISCAL INFORMATION
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Typography sx={{ width: 140, color: '#495057', fontSize: '13px' }}>
                      Fiscal Position <sup style={{ color: '#875A7B', cursor: 'help' }}>?</sup>
                    </Typography>
                    <TextField
                      name="fiscalPosition"
                      value={formData.fiscalPosition}
                      onChange={handleChange}
                      placeholder="Select..."
                      variant="standard"
                      size="small"
                      fullWidth
                      InputProps={{ disableUnderline: true, sx: { fontSize: '13px' } }}
                      sx={{ borderBottom: '1px solid #dee2e6' }}
                    />
                  </Box>
                </Box>

                {/* Right Column - Purchase */}
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography sx={{ fontWeight: 600, color: '#333', fontSize: '13px', mb: 2, pb: 1, borderBottom: '1px solid #e0e0e0' }}>
                    PURCHASE
                  </Typography>
                  <Box sx={{ mb: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                      <Typography sx={{ width: 140, color: '#495057', fontSize: '13px' }}>Buyer</Typography>
                      <TextField
                        name="buyer"
                        value={formData.buyer}
                        onChange={handleChange}
                        placeholder="Select..."
                        variant="standard"
                        size="small"
                        fullWidth
                        InputProps={{ disableUnderline: true, sx: { fontSize: '13px' } }}
                        sx={{ borderBottom: '1px solid #dee2e6' }}
                      />
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                      <Typography sx={{ width: 140, color: '#495057', fontSize: '13px' }}>Payment Terms</Typography>
                      <TextField
                        select
                        name="purchasePaymentTerms"
                        value={formData.purchasePaymentTerms}
                        onChange={handleChange}
                        variant="standard"
                        size="small"
                        fullWidth
                        InputProps={{ disableUnderline: true, sx: { fontSize: '13px' } }}
                        sx={{ borderBottom: '1px solid #dee2e6' }}
                      >
                        <MenuItem value="">Select...</MenuItem>
                        {paymentTermsList.map((term) => (
                          <MenuItem key={term._id} value={term._id}>
                            {term.name}
                          </MenuItem>
                        ))}
                      </TextField>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                      <Typography sx={{ width: 140, color: '#495057', fontSize: '13px' }}>Payment Method</Typography>
                      <TextField
                        select
                        name="purchasePaymentMethod"
                        value={formData.purchasePaymentMethod}
                        onChange={handleChange}
                        variant="standard"
                        size="small"
                        fullWidth
                        InputProps={{ disableUnderline: true, sx: { fontSize: '13px' } }}
                        sx={{ borderBottom: '1px solid #dee2e6' }}
                      >
                        <MenuItem value="">Select...</MenuItem>
                        <MenuItem value="bank_transfer">Bank Transfer</MenuItem>
                        <MenuItem value="credit_card">Credit Card</MenuItem>
                        <MenuItem value="cash">Cash</MenuItem>
                      </TextField>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                      <Typography sx={{ width: 140, color: '#495057', fontSize: '13px' }}>
                        Receipt Reminder <sup style={{ color: '#875A7B', cursor: 'help' }}>?</sup>
                      </Typography>
                      <Checkbox
                        checked={formData.receiptReminder}
                        onChange={(e) => setFormData({ ...formData, receiptReminder: e.target.checked })}
                        size="small"
                        sx={{ p: 0, color: '#00A09D', '&.Mui-checked': { color: '#00A09D' } }}
                      />
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Typography sx={{ width: 140, color: '#495057', fontSize: '13px' }}>
                        Supplier Currency <sup style={{ color: '#875A7B', cursor: 'help' }}>?</sup>
                      </Typography>
                      <TextField
                        select
                        name="supplierCurrency"
                        value={formData.supplierCurrency}
                        onChange={handleChange}
                        variant="standard"
                        size="small"
                        fullWidth
                        InputProps={{ disableUnderline: true, sx: { fontSize: '13px' } }}
                        sx={{ borderBottom: '1px solid #dee2e6' }}
                      >
                        <MenuItem value="">Select...</MenuItem>
                        <MenuItem value="INR">INR</MenuItem>
                        <MenuItem value="USD">USD</MenuItem>
                        <MenuItem value="EUR">EUR</MenuItem>
                        <MenuItem value="GBP">GBP</MenuItem>
                      </TextField>
                    </Box>
                  </Box>

                  {/* Misc */}
                  <Typography sx={{ fontWeight: 600, color: '#333', fontSize: '13px', mb: 2, pb: 1, borderBottom: '1px solid #e0e0e0' }}>
                    MISC
                  </Typography>
                  <Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                      <Typography sx={{ width: 140, color: '#495057', fontSize: '13px' }}>Reference</Typography>
                      <TextField
                        name="reference"
                        value={formData.reference}
                        onChange={handleChange}
                        placeholder="Enter reference..."
                        variant="standard"
                        size="small"
                        fullWidth
                        InputProps={{ disableUnderline: true, sx: { fontSize: '13px' } }}
                        sx={{ borderBottom: '1px solid #dee2e6' }}
                      />
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                      <Typography sx={{ width: 140, color: '#495057', fontSize: '13px' }}>Company</Typography>
                      <Typography sx={{ fontSize: '13px', color: '#017e84' }}>Visible to all</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Typography sx={{ width: 140, color: '#495057', fontSize: '13px' }}>
                        SLA Policies <sup style={{ color: '#875A7B', cursor: 'help' }}>?</sup>
                      </Typography>
                      <TextField
                        name="slaPolicies"
                        value={formData.slaPolicies}
                        onChange={handleChange}
                        placeholder="Select..."
                        variant="standard"
                        size="small"
                        fullWidth
                        InputProps={{ disableUnderline: true, sx: { fontSize: '13px' } }}
                        sx={{ borderBottom: '1px solid #dee2e6' }}
                      />
                    </Box>
                  </Box>
                </Box>
              </Box>
            )}

            {activeTab === 2 && (
              <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 4 }}>
                <Box sx={{ flex: 1 }}>
                  <Typography sx={{ fontSize: '14px', fontWeight: 600, mb: 2, textTransform: 'uppercase', color: '#666' }}>
                    General
                  </Typography>
                  <Box sx={{ display: 'flex', mb: 1.5 }}>
                    <Typography sx={{ width: 140, fontSize: '14px', color: '#666' }}>Banks</Typography>
                    <TextField
                      name="banks"
                      value={formData.banks}
                      onChange={handleChange}
                      placeholder="Add bank account..."
                      variant="standard"
                      size="small"
                      fullWidth
                      InputProps={{ disableUnderline: true, sx: { fontSize: '14px' } }}
                      sx={{ borderBottom: '1px solid #dee2e6' }}
                    />
                  </Box>
                  <Box sx={{ display: 'flex', mb: 1.5 }}>
                    <Typography sx={{ width: 140, fontSize: '14px', color: '#666' }}>Customer Type</Typography>
                    <Typography sx={{ fontSize: '14px', color: '#333', textTransform: 'capitalize' }}>
                      {formData.customerType || '-'}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', mb: 1.5 }}>
                    <Typography sx={{ width: 140, fontSize: '14px', color: '#666' }}>Lead Source</Typography>
                    <Typography sx={{ fontSize: '14px', color: '#333', textTransform: 'capitalize' }}>
                      {formData.leadSource?.replace('-', ' ') || '-'}
                    </Typography>
                  </Box>
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography sx={{ fontSize: '14px', fontWeight: 600, mb: 2, textTransform: 'uppercase', color: '#666' }}>
                    Invoice Follow-ups
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                    <Typography sx={{ width: 140, color: '#495057', fontSize: '13px' }}>Reminders</Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <RadioGroup
                        row
                        value={formData.reminders}
                        onChange={(e) => setFormData({ ...formData, reminders: e.target.value })}
                      >
                        <FormControlLabel
                          value="automatic"
                          control={<Radio size="small" sx={{ color: '#00A09D', '&.Mui-checked': { color: '#00A09D' } }} />}
                          label={<Typography sx={{ fontSize: '13px' }}>Automatic</Typography>}
                        />
                        <FormControlLabel
                          value="manual"
                          control={<Radio size="small" sx={{ color: '#6c757d' }} />}
                          label={<Typography sx={{ fontSize: '13px' }}>Manual</Typography>}
                        />
                      </RadioGroup>
                    </Box>
                  </Box>
                  <Box sx={{ display: 'flex', mb: 1.5 }}>
                    <Typography sx={{ width: 140, fontSize: '14px', color: '#666' }}>Created</Typography>
                    <Typography sx={{ fontSize: '14px', color: '#333' }}>
                      {formatDate(formData.createdAt)}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', mb: 1.5 }}>
                    <Typography sx={{ width: 140, fontSize: '14px', color: '#666' }}>Updated</Typography>
                    <Typography sx={{ fontSize: '14px', color: '#333' }}>
                      {formatDate(formData.updatedAt)}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            )}

            {activeTab === 3 && (
              <Box>
                <Typography sx={{ fontSize: '14px', fontWeight: 600, mb: 2, textTransform: 'uppercase', color: '#666' }}>
                  Internal Notes
                </Typography>
                <TextField
                  fullWidth
                  multiline
                  rows={4}
                  name="internalNotes"
                  value={formData.internalNotes}
                  onChange={handleChange}
                  placeholder="Internal notes about this customer..."
                  variant="outlined"
                  size="small"
                />
              </Box>
            )}

            {/* Save/Discard Buttons at Bottom */}
            {hasChanges && (
              <Box sx={{ mt: 3, pt: 2, borderTop: '1px solid #e0e0e0', display: 'flex', gap: 1 }}>
                <Button
                  variant="contained"
                  onClick={handleSubmit}
                  disabled={saving}
                  sx={{
                    bgcolor: '#875A7B',
                    textTransform: 'none',
                    fontWeight: 500,
                    fontSize: '13px',
                    px: 2,
                    py: 0.75,
                    minHeight: 36,
                    borderRadius: '4px',
                    '&:hover': { bgcolor: '#714B68' },
                  }}
                >
                  {saving ? 'Saving...' : 'Save'}
                </Button>
                <Button
                  variant="outlined"
                  onClick={handleDiscard}
                  disabled={saving}
                  sx={{
                    borderColor: '#dee2e6',
                    color: '#666',
                    textTransform: 'none',
                    fontWeight: 500,
                    fontSize: '13px',
                    px: 2,
                    py: 0.75,
                    minHeight: 36,
                    borderRadius: '4px',
                    backgroundColor: '#fff',
                    '&:hover': { borderColor: '#ccc', backgroundColor: '#f8f8f8' },
                  }}
                >
                  Discard
                </Button>
              </Box>
            )}
          </Paper>
        </Box>

        {/* Right - Activity Panel */}
        <Box sx={{ width: { xs: '100%', lg: 380 }, flexShrink: 0 }}>
          <ActivityPanel
            documentType="customer"
            documentId={id}
            documentNumber={formData.name}
            user={user}
            fetchActivities={async (docType, docId) => {
              try {
                const response = await activityLogAPI.getByDocument(docType, docId);
                return response.data;
              } catch (error) {
                console.error('Error fetching customer activities:', error);
                return { logs: [] };
              }
            }}
            onSendMessage={async (data) => {
              try {
                await activityLogAPI.addMessage('customer', id, data.content, formData.name);
                toast.success('Message sent');
              } catch (error) {
                toast.error('Failed to send message');
              }
            }}
            onLogNote={async (data) => {
              try {
                await activityLogAPI.addNote('customer', id, data.content, formData.name);
                toast.success('Note logged');
              } catch (error) {
                toast.error('Failed to log note');
              }
            }}
            onScheduleActivity={(data) => toast.info('Activity scheduled')}
            showFollowers
            followers={[]}
          />
        </Box>
      </Box>

      {/* Delete Confirmation */}
      <ConfirmDialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        onConfirm={confirmDelete}
        title="Delete Customer"
        message={`Are you sure you want to delete "${formData.name}"? This action cannot be undone.`}
        type="danger"
        loading={deleting}
      />
    </Box>
  );
};

export default CustomerView;
